import { useRef, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { ImagePlus, Loader2, Trash2, Upload } from "lucide-react";

const PRE_RESIZE_SIDE = 1600;

async function preResize(file) {
  const bitmap = await createImageBitmap(file).catch(() => null);
  if (!bitmap) return file;
  const scale = Math.min(1, PRE_RESIZE_SIDE / Math.max(bitmap.width, bitmap.height));
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(bitmap.width * scale);
  canvas.height = Math.round(bitmap.height * scale);
  canvas.getContext("2d").drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  const blob = await new Promise((r) => canvas.toBlob(r, "image/jpeg", 0.85));
  return blob && blob.size < file.size ? new File([blob], "image.jpg", { type: "image/jpeg" }) : file;
}

const formatKB = (b) => `${Math.max(1, Math.round(b / 1024))} KB`;

export const ImageDropzone = ({ label, value, onChange, testid }) => {
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const [progress, setProgress] = useState(null);
  const [info, setInfo] = useState("");

  const handleFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("File harus berupa gambar");
      return;
    }
    setProgress(0);
    try {
      const small = await preResize(file);
      const form = new FormData();
      form.append("file", small);
      const { data } = await api.post("/upload/image", form, {
        onUploadProgress: (e) => e.total && setProgress(Math.round((e.loaded / e.total) * 100)),
      });
      setInfo(`${formatKB(file.size)} → ${formatKB(data.size)}`);
      onChange({ url: data.url, key: data.key });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload gagal");
    } finally {
      setProgress(null);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    handleFile(e.dataTransfer.files?.[0]);
  };

  const uploading = progress !== null;

  return (
    <div className="space-y-1.5" data-testid={testid}>
      <span className="text-sm font-medium">{label}</span>
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        className={`relative flex h-40 flex-col items-center justify-center gap-2 overflow-hidden rounded-md border-2 border-dashed p-3 text-center transition-colors ${
          dragging ? "border-primary bg-primary/5" : "border-border bg-surface/40"
        }`}
        data-testid={`${testid}-dropzone`}
      >
        {value ? (
          <img src={value} alt={label} className="h-full w-full object-contain" data-testid={`${testid}-preview`} />
        ) : uploading ? null : (
          <>
            <ImagePlus className="h-6 w-6 text-muted-foreground" />
            <p className="text-xs text-muted-foreground">Seret & lepas gambar di sini</p>
          </>
        )}
        {uploading && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-background/80 px-6">
            <Loader2 className="h-5 w-5 animate-spin text-primary" />
            <Progress value={progress} className="h-1.5" />
            <span className="text-xs text-muted-foreground">Mengunggah... {progress}%</span>
          </div>
        )}
      </div>
      <div className="flex items-center gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={uploading}
          onClick={() => inputRef.current?.click()}
          data-testid={`${testid}-pick-button`}
        >
          <Upload className="mr-1.5 h-3.5 w-3.5" />
          {value ? "Ganti" : "Pilih Gambar"}
        </Button>
        {value && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={uploading}
            onClick={() => {
              setInfo("");
              onChange(null);
            }}
            data-testid={`${testid}-remove-button`}
          >
            <Trash2 className="mr-1.5 h-3.5 w-3.5" />
            Hapus
          </Button>
        )}
        {info && <span className="ml-auto text-[11px] text-muted-foreground" data-testid={`${testid}-size-info`}>{info}</span>}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => {
          handleFile(e.target.files?.[0]);
          e.target.value = "";
        }}
        data-testid={`${testid}-file-input`}
      />
    </div>
  );
};
